import { skipToken, useQuery } from "@tanstack/react-query";

import { appointmentExchangesApi } from "@/api";
import { QueryKey } from "@/query/Query";

export interface ExchangeableRange {
  from: Date;
  till: Date;
}

/**
 * The logged-in counselor's own Termine that can still be offered for a swap within the date range.
 * Nothing is requested while the range is incomplete or invalid (`null`). Nested under the exchanges
 * query key on purpose: creating a request invalidates that prefix, so the offered Termin drops out of
 * this list without the mutation having to know about it. `isError` lets the picker tell a failed
 * request from a range that genuinely has no Termine.
 */
export function useExchangeableAppointmentsQuery(range: ExchangeableRange | null) {
  const {
    data: appointments = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      QueryKey.AppointmentExchanges,
      "exchangeable",
      range?.from.getTime() ?? null,
      range?.till.getTime() ?? null,
    ],
    queryFn:
      range === null
        ? skipToken
        : () => appointmentExchangesApi.getExchangeableAppointments({ from: range.from, till: range.till }),
  });

  return { appointments, isLoading, isError };
}
